type EnvConfig = {
  apiBaseUrl: string
  cognitoDomain: string
  userPoolClientId: string
  appOrigin?: string
}

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]'
}

function required(name: string, value: string | undefined): string {
  const trimmed = value?.trim()
  if (!trimmed) {
    throw new Error(`Missing required environment variable: ${name}`)
  }
  return trimmed
}

function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export function getEnvConfig(): EnvConfig {
  return {
    apiBaseUrl: required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL),
    cognitoDomain: required('VITE_COGNITO_DOMAIN', import.meta.env.VITE_COGNITO_DOMAIN),
    userPoolClientId: required(
      'VITE_COGNITO_USER_POOL_CLIENT_ID',
      import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
    ),
    appOrigin: optional(import.meta.env.VITE_APP_ORIGIN),
  }
}

export function getAuthRedirectUri(path = '/auth/callback'): string {
  const { appOrigin } = getEnvConfig()
  if (typeof window !== 'undefined') {
    const runtimeOrigin = window.location.origin
    if (!appOrigin) {
      return `${runtimeOrigin}${path}`
    }
    try {
      const runtimeUrl = new URL(runtimeOrigin)
      const configuredUrl = new URL(appOrigin)
      if (
        import.meta.env.DEV &&
        isLoopbackHost(runtimeUrl.hostname) &&
        isLoopbackHost(configuredUrl.hostname) &&
        runtimeUrl.origin !== configuredUrl.origin
      ) {
        // Keep PKCE sessionStorage on the same origin during local development.
        return `${runtimeOrigin}${path}`
      }
      return `${configuredUrl.origin}${path}`
    } catch {
      return `${runtimeOrigin}${path}`
    }
  }
  if (appOrigin) {
    return `${appOrigin}${path}`
  }
  throw new Error('Unable to derive auth redirect URI on server. Set VITE_APP_ORIGIN.')
}

export function getLogoutUri(path = '/'): string {
  const { appOrigin } = getEnvConfig()
  if (typeof window !== 'undefined') {
    const runtimeOrigin = window.location.origin
    if (!appOrigin) {
      return `${runtimeOrigin}${path}`
    }
    try {
      const runtimeUrl = new URL(runtimeOrigin)
      const configuredUrl = new URL(appOrigin)
      if (
        import.meta.env.DEV &&
        isLoopbackHost(runtimeUrl.hostname) &&
        isLoopbackHost(configuredUrl.hostname) &&
        runtimeUrl.origin !== configuredUrl.origin
      ) {
        return `${runtimeOrigin}${path}`
      }
      return `${configuredUrl.origin}${path}`
    } catch {
      return `${runtimeOrigin}${path}`
    }
  }
  if (appOrigin) {
    return `${appOrigin}${path}`
  }
  throw new Error('Unable to derive logout URI on server. Set VITE_APP_ORIGIN.')
}
